import { apiClient } from './apiClient';
import { Logger } from '../utils/logger';

export interface QuotaUsage {
  credits_used: number;
  credits_limit: number;
  purchased_credits_balance: number;
  percentage_used: number;
  period: string;
  period_start: string;
  period_end: string;
  quota_exceeded: boolean;
  enforcement_enabled: boolean;
  plan_name?: string;
  plan_display_name?: string;
  has_watermark?: boolean;
  max_sources?: number | null;
  max_channels?: number | null;
  can_purchase_topup?: boolean;
}

export interface TopupInfo {
  plan_name: string;
  plan_display_name: string;
  can_purchase_topup: boolean;
  topup_credits_per_unit: number;
  topup_price_cents: number;
  purchased_credits_balance: number;
  period: string;
}

export interface TopupResult {
  topup_id: string;
  payment_status: string;
  credits_amount: number;
  price_amount_cents: number;
  unit_count: number;
  checkout_url: string | null;
}

/**
 * Récupère la consommation AI du mois courant.
 * Inclut les quotas effectifs (plan + topups).
 */
export async function getQuotaUsage(): Promise<QuotaUsage> {
  try {
    const response = await apiClient.get<QuotaUsage>('/quota/usage');
    return response.data;
  } catch (error) {
    Logger.error('[QuotaService] Failed to fetch quota usage', error);
    // Fallback gracieux — ne bloque pas l'UI
    return {
      credits_used: 0,
      credits_limit: 0,
      purchased_credits_balance: 0,
      percentage_used: 0,
      period: '',
      period_start: '',
      period_end: '',
      quota_exceeded: false,
      enforcement_enabled: false,
    };
  }
}

/**
 * Récupère les informations pour la modale de topup.
 */
export async function getTopupInfo(): Promise<TopupInfo> {
  const response = await apiClient.get<TopupInfo>('/quota/topup-info');
  return response.data;
}

/**
 * Achète une recharge de quotas AI.
 * Phase MVP: activation immédiate. Phase Stripe: retourne un checkout_url.
 */
export async function purchaseTopup(unitCount: number = 1): Promise<TopupResult> {
  const response = await apiClient.post<TopupResult>('/quota/topup', { unit_count: unitCount });
  return response.data;
}

/**
 * Liste les topups de l'utilisateur pour le mois courant.
 */
export async function getUserTopups(period?: string): Promise<{ items: TopupResult[]; total: number }> {
  const params = period ? { period } : {};
  const response = await apiClient.get<{ items: TopupResult[]; total: number }>('/quota/topups', { params });
  return response.data;
}
